import { useState } from "react";
import { cn } from "@/lib/utils";

export function CrmContactInfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-[var(--color-muted-foreground)]">{label}: </span>
      <span className="text-[var(--color-foreground)]">{value}</span>
    </div>
  );
}

export interface CrmContactDetailFactItem {
  label: string;
  value: string;
}

export function CrmContactDetailFactsList({
  items,
  className,
}: {
  items: readonly CrmContactDetailFactItem[];
  className?: string;
}) {
  if (items.length === 0) return null;

  return (
    <div className={cn("space-y-3", className)}>
      {items.map((item) => (
        <CrmContactInfoItem key={item.label} label={item.label} value={item.value} />
      ))}
    </div>
  );
}

export function CrmEditableContactInfoItem({
  label,
  value,
  field,
  onSave,
}: {
  label: string;
  value: string;
  field: string;
  onSave: (field: string, value: string) => Promise<void> | void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleBlur = async () => {
    setIsEditing(false);
    if (editValue !== value) {
      await onSave(field, editValue);
    }
  };

  if (isEditing) {
    return (
      <div>
        <span className="text-[var(--color-muted-foreground)]">{label}: </span>
        <input
          type="text"
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          onBlur={handleBlur}
          onKeyDown={(event) => {
            if (event.key === "Enter") void handleBlur();
            if (event.key === "Escape") {
              setEditValue(value);
              setIsEditing(false);
            }
          }}
          autoFocus
          className="inline-block w-auto border-b border-[var(--color-primary)] bg-transparent text-sm text-[var(--color-foreground)] outline-none"
        />
      </div>
    );
  }

  return (
    <div
      className="cursor-pointer hover:bg-[var(--color-muted)]/50 rounded px-1 -mx-1 transition-colors"
      onClick={() => setIsEditing(true)}
      title="Click to edit"
    >
      <span className="text-[var(--color-muted-foreground)]">{label}: </span>
      <span className="text-[var(--color-foreground)]">{value || "\u2014"}</span>
    </div>
  );
}

export interface CrmContactDetailTabOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export function CrmContactDetailTabs<TValue extends string>({
  tabs,
  activeTab,
  onTabChange,
  className,
}: {
  tabs: readonly CrmContactDetailTabOption<TValue>[];
  activeTab: TValue;
  onTabChange: (value: TValue) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex border-b border-[var(--color-border)]", className)}>
      {tabs.map((tab) => (
        <CrmContactDetailTabButton
          key={tab.value}
          label={tab.label}
          active={activeTab === tab.value}
          onClick={() => onTabChange(tab.value)}
        />
      ))}
    </div>
  );
}

export function CrmContactDetailTabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "stagger-item flex-1 px-4 py-2.5 text-sm font-medium transition-colors",
        active
          ? "border-b-2 border-[var(--color-primary)] text-[var(--color-foreground)]"
          : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
      )}
    >
      {label}
    </button>
  );
}
